import React, { useEffect, useState, useRef } from "react";
import axios from "axios";
import classNames from "classnames";
import { getApiUrl } from '../../../utils/apiConfig';


const PUBLICA_OPCIONES = ["Biblioteca", "Centro de salud", "Colegio"];

/**
 * Props:
 * - selectedObra: { IdTipoObra?, TipoObraNombre?, TipoObraInfoExtra? }
 * - setSelectedObra: fn
 * - disabled?: boolean
 */
const TipoObraDropdown = ({ selectedObra, setSelectedObra, disabled }) => {
  const [tipos, setTipos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef(null);

  // Cargar tipos de obra
  useEffect(() => {
    let abort = false;
    const load = async () => {
      try {
        setLoading(true);
        const res = await axios.get(getApiUrl('/api/tiposObra'));
        // Esperado: [{ IdTipoObra, Nombre, ExtraEtiqueta }]
        const data = Array.isArray(res.data) ? res.data : [];
        if (!abort) setTipos(data);
      } catch (e) {
        console.error("[TipoObraDropdown] Error cargando tipos:", e?.message || e);
        if (!abort) setTipos([]);
      } finally {
        if (!abort) setLoading(false);
      }
    };
    load();
    return () => { abort = true; };
  }, []);

  // Cerrar dropdown al hacer clic fuera
  useEffect(() => {
    if (!isOpen) return;
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  // Encontrar el tipo seleccionado
  const tipoSeleccionado = tipos.find(t => t.IdTipoObra === selectedObra?.IdTipoObra);
  const hasSelection = Boolean(tipoSeleccionado);
  const placeholder = 'Selecciona tipo de obra';

  const onSelect = (tipo) => {
    // Cuando cambias de tipo, resetea el extra salvo casos especiales
    let extra = "";
    if (tipo.Nombre?.toUpperCase() === "OBRA PÚBLICA") {
      // por defecto, primera opción del select
      extra = PUBLICA_OPCIONES[0];
    }
    setSelectedObra({
      ...selectedObra,
      IdTipoObra: tipo.IdTipoObra,
      TipoObraNombre: tipo.Nombre?.trim() || "",
      TipoObraInfoExtra: extra,
    });
    setIsOpen(false);
  };

  const onChangeExtra = (value) => {
    setSelectedObra({
      ...selectedObra,
      TipoObraInfoExtra: value,
    });
  };

  // Determinar si el tipo seleccionado requiere campo extra
  const nombre = (tipoSeleccionado?.Nombre || "").trim();
  const extraEtiqueta = tipoSeleccionado?.ExtraEtiqueta?.trim() || null;
  const isPublica = nombre.toUpperCase() === "OBRA PÚBLICA";
  const isOtros = nombre.toUpperCase() === "OTROS";
  const isReformaIntegral = nombre.toUpperCase() === "REFORMA INTEGRAL";
  const requiereExtra = isPublica || isOtros || isReformaIntegral || extraEtiqueta;

  return (
    <div className="w-full flex flex-col gap-3">
      {/* Dropdown principal */}
      <div className="relative" ref={containerRef}>
        <label className="block mb-1 text-xs font-medium text-gray-700 truncate">
          Tipo de Obra*
        </label>
        <button
          disabled={disabled || loading}
          onClick={() => setIsOpen(!isOpen)}
          title={hasSelection ? tipoSeleccionado.Nombre : placeholder}
          className="DropDownName w-full p-2 border rounded shadow-sm bg-white text-left flex justify-between items-center"
        >
          <span className={`truncate ${hasSelection ? '' : 'text-gray-400'}`}>
            {loading ? 'Cargando...' : hasSelection ? tipoSeleccionado.Nombre : placeholder}
          </span>
          <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </button>
        {isOpen && !loading && (
          <ul className="absolute mt-1 w-full bg-white border rounded shadow-lg max-h-60 overflow-auto" style={{ zIndex: 1100 }} role="listbox">
            <li
              onClick={() => { 
                setSelectedObra({ 
                  ...selectedObra, 
                  IdTipoObra: null, 
                  TipoObraNombre: '', 
                  TipoObraInfoExtra: '' 
                }); 
                setIsOpen(false); 
              }}
              className={`cursor-pointer px-4 py-2 text-sm hover:bg-blue-50 hover:text-blue-700 transition-colors ${!hasSelection ? 'bg-blue-50 text-blue-700 font-medium' : ''}`}
              role="option"
              aria-selected={!hasSelection}
            >
              {placeholder}
            </li>
            {tipos.map(tipo => (
              <li
                key={tipo.IdTipoObra}
                onClick={() => onSelect(tipo)}
                className={classNames(
                  'cursor-pointer px-4 py-2 text-sm hover:bg-blue-50 hover:text-blue-700 transition-colors',
                  { 'bg-blue-50 text-blue-700 font-medium': selectedObra?.IdTipoObra === tipo.IdTipoObra }
                )}
                role="option"
                aria-selected={selectedObra?.IdTipoObra === tipo.IdTipoObra}
              >
                {tipo.Nombre}
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Campo extra dinámico */}
      {hasSelection && requiereExtra && (
        <div className="w-full">
          {isPublica ? (
            // Dropdown para Obra Pública
            <div>
              <label className="block mb-1 text-xs font-medium text-gray-700 truncate">
                Especifica el tipo*
              </label>
              <DropdownPublica
                options={PUBLICA_OPCIONES}
                value={selectedObra?.TipoObraInfoExtra || ''}
                onChange={onChangeExtra}
                placeholder="Selecciona tipo"
                disabled={disabled}
              />
            </div>
          ) : isOtros || isReformaIntegral ? (
            // Textarea para Otros y Reforma Integral
            <div>
              <label className="block mb-1 text-xs font-medium text-gray-700 truncate">
                {isOtros ? 'Especifica el tipo*' : extraEtiqueta || 'Describe la reforma*'}
              </label>
              <textarea
                disabled={disabled}
                className="truncatecomentarios border rounded p-2 text-sm bg-white w-full inputModal shadow-sm resize-y min-h-[60px]"
                placeholder={isOtros ? "Describe el tipo de obra" : extraEtiqueta || "Describe la reforma"}
                value={selectedObra?.TipoObraInfoExtra || ""}
                onChange={(e) => onChangeExtra(e.target.value)}
              />
            </div>
          ) : extraEtiqueta ? (
            // Input numérico para otros casos con ExtraEtiqueta
            <div>
              <label className="block mb-1 text-xs font-medium text-gray-700 truncate">
                {extraEtiqueta}*
              </label>
              <input
                type="tel"
                inputMode="numeric"
                pattern="[0-9]{1,5}"
                maxLength={5}
                disabled={disabled}
                className="inputModal shadow-sm w-full p-2 border rounded bg-white"
                placeholder={extraEtiqueta}
                value={selectedObra?.TipoObraInfoExtra || ""}
                onChange={(e) => {
                  const valor = e.target.value.replace(/\D/g, '');
                  if (valor.length <= 5) {
                    onChangeExtra(valor);
                  }
                }}
              />
            </div>
          ) : null}
        </div>
      )}
    </div>
  );
};

// Componente para el dropdown de Obra Pública
function DropdownPublica({ options, value, onChange, placeholder, disabled }) {
  const [open, setOpen] = useState(false);
  const ref = useRef();

  useEffect(() => {
    if (!open) return;
    const handleClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open]);

  const hasValue = Boolean(value);

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        disabled={disabled}
        className="DropDownName w-full p-2 border rounded shadow-sm bg-white text-left flex justify-between items-center"
        onClick={() => setOpen(!open)}
      >
        <span className={`truncate ${hasValue ? '' : 'text-gray-400'}`}>
          {value || placeholder}
        </span>
        <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>
      {open && (
        <ul className="absolute z-10 mt-1 w-full bg-white border rounded shadow-lg max-h-60 overflow-auto">
          <li
            className={`cursor-pointer px-4 py-2 text-sm hover:bg-blue-50 hover:text-blue-700 transition-colors ${!hasValue ? 'bg-blue-50 text-blue-700 font-medium' : ''}`}
            onClick={() => {
              onChange('');
              setOpen(false);
            }}
          >
            {placeholder}
          </li>
          {options.map((opt) => (
            <li
              key={opt}
              className={`cursor-pointer px-4 py-2 text-sm hover:bg-blue-50 hover:text-blue-700 transition-colors ${value === opt ? 'bg-blue-50 text-blue-700 font-medium' : ''}`}
              onClick={() => {
                onChange(opt);
                setOpen(false);
              }}
            >
              {opt}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export default TipoObraDropdown;
